import {useEffect, useMemo, useRef, useState} from 'react'
import Overlay from './Overlay'
import {
  IconCheck, IconChevronDown, IconChevronRight, IconMapPin, IconPencil, IconPlus, IconSearch,
  IconTrash, IconX,
} from '@tabler/icons-react'
import {
  badCoord, createPlace, deletePlace, placeOptions, placePeople, placeUsers, typeLabel, typeRu,
  typeValue, updatePlace, PLACE_TYPES, type PlaceTypeSel,
} from './api/places'
import {fullName, lifespan, pathMatches, placeChildren, placePath, searchWords} from './api/tree'
import type {TreeIndex} from './api/types'
import PlacesMap from './PlacesMap'
import Portrait from './Portrait'

interface Props {
  ix: TreeIndex
  onChanged: () => void
  onOpenPerson: (handle: string) => void
}

interface Draft {
  name: string
  type: PlaceTypeSel
  parent: string | null
  lat: string
  long: string
}

const emptyDraft = (): Draft => ({name: '', type: PLACE_TYPES[2], parent: null, lat: '', long: ''})

export default function PlacesList({ix, onChanged, onOpenPerson}: Props) {
  const [q, setQ] = useState('')
  const [add, setAdd] = useState<Draft>(emptyDraft())
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft())
  const [err, setErr] = useState('')
  /* Расстановка точки живёт здесь, а не в карте: начинают её из строки
     места — иначе кнопку под картой никто не находит. */
  const [pinning, setPinning] = useState<string | null>(null)
  const [pinStatus, setPinStatus] = useState<{ok: boolean; text: string} | null>(null)
  const mapBox = useRef<HTMLDivElement>(null)

  function startPin(handle: string) {
    setPinning(handle)
    setPinStatus(null)
    mapBox.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
  }
  const [busy, setBusy] = useState(false)
  // по умолчанию всё свёрнуто: справочник разрастётся, а стран немного
  const [open, setOpen] = useState<Set<string>>(new Set())

  const [showPeople, setShowPeople] = useState<string | null>(null)

  const peopleAt = useMemo(() => placePeople(ix), [ix])
  const usage = useMemo(
    () => new Map([...peopleAt].map(([h, set]) => [h, set.size])),
    [peopleAt],
  )
  const options = useMemo(() => placeOptions(ix), [ix])

  const kidsOfPlace = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const h of ix.places.keys()) {
      const p = ix.places.get(h)!.parent
      const key = p || ''
      const list = m.get(key)
      if (list) list.push(h)
      else m.set(key, [h])
    }
    for (const list of m.values()) {
      list.sort((a, b) => (ix.places.get(a)?.name || '').localeCompare(ix.places.get(b)?.name || '', 'ru'))
    }
    return m
  }, [ix])

  const hasKids = (h: string) => (kidsOfPlace.get(h) || []).length > 0

  /** Плоский список с уровнем вложенности — дерево рисуем отступами.
   *  При поиске сворачивание игнорируем и подмешиваем предков совпавших,
   *  иначе найденную улицу не видно из-за свёрнутой страны. */
  const shown = useMemo(() => {
    const v = q.trim().toLowerCase()
    let allowed: Set<string> | null = null
    if (v) {
      allowed = new Set<string>()
      for (const h of ix.places.keys()) {
        if (!pathMatches(placePath(ix, h), searchWords(q))) continue
        let cur: string | null = h
        for (let i = 0; cur && i < 10; i++) {
          allowed.add(cur)
          cur = ix.places.get(cur)?.parent ?? null
        }
      }
    }

    const out: {handle: string; depth: number}[] = []
    const walk = (parent: string | null, depth: number) => {
      for (const h of kidsOfPlace.get(parent || '') || []) {
        if (allowed && !allowed.has(h)) continue
        out.push({handle: h, depth})
        if (allowed || open.has(h)) walk(h, depth + 1)
      }
    }
    walk(null, 0)

    /* Осиротевшие: родитель указан, но его в справочнике нет. Обход идёт
     * сверху вниз и до них не доберётся — покажем на верхнем уровне, иначе
     * пропадут совсем. Просто свёрнутые узлы сюда не попадают: у них
     * родитель на месте. */
    const seen = new Set(out.map(o => o.handle))
    for (const h of ix.places.keys()) {
      if (seen.has(h)) continue
      const parent = ix.places.get(h)!.parent
      if (!parent || ix.places.has(parent)) continue
      if (allowed && !allowed.has(h)) continue
      out.push({handle: h, depth: 0})
    }
    return out
  }, [ix, q, open, kidsOfPlace])

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setErr('')
    try {
      await fn()
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = (h: string, title: string) =>
    run(async () => {
      // место бывает и родителем других мест, и привязанным к событиям
      const kids = placeChildren(ix, h)
      if (kids.length) throw new Error(`«${title}» — родитель для ${kids.length} мест, сначала перенесите их`)
      const users = await placeUsers(h)
      if (users.length) throw new Error(`«${title}» привязано к ${users.length} событиям — сначала отвяжите`)
      await deletePlace(h)
    })

  const typeSelect = (val: PlaceTypeSel, on: (v: PlaceTypeSel) => void) => (
    <select
      className="sel"
      value={PLACE_TYPES.findIndex(t => t.value === val.value && t.string === val.string)}
      onChange={e => on(PLACE_TYPES[Number(e.target.value)] || PLACE_TYPES[PLACE_TYPES.length - 1])}
    >
      {PLACE_TYPES.map((t, i) => <option key={i} value={i}>{typeLabel(t)}</option>)}
    </select>
  )

  const coordInputs = (d: Draft, on: (patch: Partial<Draft>) => void) => (
    <>
      <input className={'coord' + (badCoord(d.lat) ? ' bad' : '')} value={d.lat}
             onChange={e => on({lat: e.target.value})} placeholder="широта" />
      <input className={'coord' + (badCoord(d.long) ? ' bad' : '')} value={d.long}
             onChange={e => on({long: e.target.value})} placeholder="долгота" />
    </>
  )

  const parentSelect = (val: string | null, on: (v: string | null) => void, skip?: string) => (
    <select className="sel" value={val || ''} onChange={e => on(e.target.value || null)}>
      <option value="">— верхний уровень —</option>
      {options.filter(([h]) => h !== skip).map(([h, t]) => <option key={h} value={h}>{t}</option>)}
    </select>
  )

  return (
    <div className="page">
      <div className="pagehead">
        <h1>Места</h1>
        <div className="muted">{ix.places.size} в справочнике</div>
      </div>

      <div className="toolbar">
        <div className="search wide">
          <IconSearch size={15} className="ico" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Название" />
        </div>
        <button className="btn" onClick={() => setOpen(new Set(ix.places.keys()))}>Развернуть всё</button>
        <button className="btn" onClick={() => setOpen(new Set())}>Свернуть всё</button>
      </div>

      <div className="addbox">
        <input
          value={add.name}
          onChange={e => setAdd({...add, name: e.target.value})}
          placeholder="Новое место — «Мерефа» или «ул. Садовая 12»"
        />
        {typeSelect(add.type, v => setAdd({...add, type: v}))}
        {parentSelect(add.parent, v => setAdd({...add, parent: v}))}
        {coordInputs(add, patch => setAdd({...add, ...patch}))}
        <button
          className="btn primary"
          disabled={busy || !add.name.trim() || badCoord(add.lat) || badCoord(add.long)}
          onClick={() =>
            run(async () => {
              await createPlace(add.name, add.type, add.parent, {lat: add.lat, long: add.long})
              setAdd({...emptyDraft(), type: add.type, parent: add.parent})
            })
          }
        >
          <IconPlus size={15} stroke={1.7} /> Добавить
        </button>
      </div>

      {err && <div className="err inline" style={{marginBottom: 12}}>{err}</div>}

      <div className="rows">
        {shown.map(({handle, depth}) => {
          const node = ix.places.get(handle)!
          const isEdit = editing === handle
          return (
            <div
              className={'plrow' + (isEdit ? '' : ' clickable')}
              key={handle}
              onClick={() => !isEdit && setShowPeople(handle)}
              title={isEdit ? undefined : 'Показать людей, связанных с этим местом'}
            >
              {/* по одной направляющей на уровень вложенности: отступ
                  получается сам и одинаковый у всех строк уровня */}
              {Array.from({length: depth}, (_, i) => <span className="guide" key={i} />)}
              {/* Колонка под стрелку есть ВСЕГДА, даже когда вложений нет.
                  Иначе город с улицами и город без них съезжают друг
                  относительно друга, и уровни перестают читаться. */}
              <span className="foldslot">
                {hasKids(handle) && (
                  <button
                    className="fold"
                    title={open.has(handle) ? 'Свернуть' : 'Развернуть'}
                    onClick={e => {
                      e.stopPropagation()
                      setOpen(s2 => {
                        const n = new Set(s2)
                        if (n.has(handle)) n.delete(handle)
                        else n.add(handle)
                        return n
                      })
                    }}
                  >
                    {open.has(handle)
                      ? <IconChevronDown size={15} stroke={2} />
                      : <IconChevronRight size={15} stroke={2} />}
                  </button>
                )}
              </span>
              <IconMapPin size={16} stroke={1.7} className="pmark" />

              {isEdit ? (
                <>
                  <input
                    className="plinput"
                    value={draft.name}
                    autoFocus
                    onChange={e => setDraft({...draft, name: e.target.value})}
                  />
                  {typeSelect(draft.type, v => setDraft({...draft, type: v}))}
                  {parentSelect(draft.parent, v => setDraft({...draft, parent: v}), handle)}
                  {coordInputs(draft, patch => setDraft({...draft, ...patch}))}
                  <button
                    className="icobtn accent"
                    title="Сохранить"
                    disabled={busy || !draft.name.trim() || badCoord(draft.lat) || badCoord(draft.long)}
                    onClick={() => run(async () => { await updatePlace(handle, draft); setEditing(null) })}
                  >
                    <IconCheck size={16} stroke={1.8} />
                  </button>
                  <button className="icobtn" title="Отмена" onClick={() => setEditing(null)}>
                    <IconX size={16} stroke={1.8} />
                  </button>
                </>
              ) : (
                <>
                  <span className="pltitle">
                    {node.name}
                    {hasKids(handle) && !open.has(handle) && (
                      <em className="inside">{(kidsOfPlace.get(handle) || []).length}</em>
                    )}
                  </span>
                  <span className="pltype">{typeRu(node.type)}</span>
                  {(node.lat || node.long) && (
                    <span className="plgeo" title="Координаты">{node.lat}, {node.long}</span>
                  )}
                  <span className="plcount">
                    {usage.get(handle) ? `${usage.get(handle)} чел.` : <span className="muted">—</span>}
                  </span>
                  <button
                    className={'icobtn' + (pinning === handle ? ' accent' : '')}
                    title={node.lat && node.long ? 'Переставить точку на карте' : 'Отметить на карте'}
                    onClick={e => { e.stopPropagation(); startPin(handle) }}
                  >
                    <IconMapPin size={16} stroke={1.7} />
                  </button>
                  <button
                    className="icobtn"
                    title="Изменить"
                    onClick={e => {
                      e.stopPropagation()
                      setEditing(handle)
                      setDraft({
                        name: node.name,
                        type: PLACE_TYPES.find(t => t.value === typeValue(node.type) && !t.string)
                          || {value: typeValue(node.type), string: node.type},
                        parent: node.parent,
                        lat: node.lat,
                        long: node.long,
                      })
                    }}
                  >
                    <IconPencil size={16} stroke={1.7} />
                  </button>
                  <button className="icobtn" title="Удалить" disabled={busy}
                          onClick={e => { e.stopPropagation(); remove(handle, node.name) }}>
                    <IconTrash size={16} stroke={1.7} />
                  </button>
                </>
              )}
            </div>
          )
        })}
        {!shown.length && <div className="empty">Ничего не нашлось</div>}
      </div>

      {/* Карта под списком, а не вместо него: справочник иерархический,
          а на карте иерархии нет — страна и улица в ней равны точке. */}
      <div ref={mapBox}>
        <PlacesMap
          ix={ix}
          usage={usage}
          pinning={pinning}
          onPinning={setPinning}
          status={pinStatus}
          onOpenPlace={h => setShowPeople(h)}
          onPin={async (h, lat, long) => {
            setBusy(true)
            setPinStatus(null)
            try {
              await updatePlace(h, {lat, long})
              setPinStatus({ok: true, text: `«${placePath(ix, h)}» отмечено: ${lat}, ${long}`})
              onChanged()
            } catch (e) {
              setPinStatus({
                ok: false,
                text: e instanceof Error ? e.message : 'Не удалось записать координаты',
              })
            } finally {
              setBusy(false)
            }
          }}
        />
      </div>

      {showPeople && (
        <PeopleAtPlace
          ix={ix}
          place={showPeople}
          handles={[...(peopleAt.get(showPeople) || [])]}
          onClose={() => setShowPeople(null)}
          onOpenPerson={onOpenPerson}
        />
      )}
    </div>
  )
}

/** Кто связан с местом — включая вложенные: у страны показываются все,
 *  кто привязан к её городам и улицам, иначе список был бы пуст у всего,
 *  кроме самого нижнего уровня. */
function PeopleAtPlace({
  ix, place, handles, onClose, onOpenPerson,
}: {
  ix: TreeIndex
  place: string
  handles: string[]
  onClose: () => void
  onOpenPerson: (h: string) => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const list = handles
    .map(h => ix.people.get(h))
    .filter((p): p is NonNullable<typeof p> => !!p)
    .sort((a, b) => fullName(a).localeCompare(fullName(b), 'ru'))

  return (
    <Overlay><div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="mhead">
          <h2>{placePath(ix, place)}</h2>
          <button type="button" className="close" onClick={onClose} aria-label="Закрыть">
            <IconX size={18} stroke={1.8} />
          </button>
        </div>

        <div className="muted" style={{marginTop: -6}}>
          {list.length ? `${list.length} чел., включая вложенные места` : 'С этим местом никто не связан'}
        </div>

        {list.length > 0 && (
          <div className="atplace">
            {list.map(p => (
              <button key={p.handle} className="mini" onClick={() => { onOpenPerson(p.handle); onClose() }}>
                <Portrait p={p} size={32} />
                <span className="mn">{fullName(p)}</span>
                <span className="md">{lifespan(p)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div></Overlay>
  )
}
